import { Check, CircleDashed, CircleHelp, Minus, Repeat, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatScore } from "@/lib/format";

/**
 * Statuts d'un appel — plan de modernisation UX, §2 et §10.
 *
 * Chaque statut associe un symbole et un texte à sa couleur : l'information
 * reste lisible sans distinguer les couleurs (WCAG 2.2, critère 1.4.1). Les
 * valeurs absentes sont affichées « Non évalué », jamais confondues avec zéro.
 */

type Tone = "success" | "warning" | "danger" | "neutral" | "muted";

const TONE_CLASS: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-destructive",
  neutral: "text-foreground",
  muted: "text-muted-foreground",
};

interface StatusTextProps {
  tone: Tone;
  icon: LucideIcon;
  children: React.ReactNode;
  className?: string;
  title?: string;
}

export function StatusText({ tone, icon: Icon, children, className, title }: StatusTextProps) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", className)} title={title}>
      <Icon className={cn("h-4 w-4 shrink-0", TONE_CLASS[tone])} aria-hidden="true" />
      <span className={cn(tone === "muted" ? "text-muted-foreground" : "text-foreground")}>{children}</span>
    </span>
  );
}

export const NotEvaluated = ({ className }: { className?: string }) => (
  <span className={cn("text-sm text-muted-foreground", className)}>Non évalué</span>
);

/* ─── Résultat ─── */

export function resolutionTone(status: string | null | undefined): { tone: Tone; icon: LucideIcon; label: string } | null {
  if (!status) return null;
  const lower = status.toLowerCase();
  if (lower.includes("complètement")) return { tone: "success", icon: Check, label: "Résolu" };
  if (lower.includes("partiel")) return { tone: "warning", icon: CircleDashed, label: "Partiellement résolu" };
  if (lower.includes("transf")) return { tone: "neutral", icon: Minus, label: "Transféré" };
  if (lower.includes("non")) return { tone: "danger", icon: X, label: "Non résolu" };
  return { tone: "neutral", icon: CircleHelp, label: status };
}

export function ResolutionStatus({ status, className }: { status: string | null | undefined; className?: string }) {
  const config = resolutionTone(status);
  if (!config) return <NotEvaluated className={className} />;
  return <StatusText tone={config.tone} icon={config.icon} className={className} title={status ?? undefined}>{config.label}</StatusText>;
}

export function ConversionStatus({ converted, className }: { converted: boolean | null | undefined; className?: string }) {
  if (converted === null || converted === undefined) return <NotEvaluated className={className} />;
  return converted
    ? <StatusText tone="success" icon={Check} className={className}>Converti</StatusText>
    : <StatusText tone="muted" icon={Minus} className={className}>Non converti</StatusText>;
}

/* ─── Satisfaction estimée par l'IA (1 à 5) ─── */

const SATISFACTION_LABELS: Record<number, string> = {
  1: "Très insatisfait",
  2: "Insatisfait",
  3: "Neutre",
  4: "Satisfait",
  5: "Très satisfait",
};

export const satisfactionLabel = (score: number | null | undefined): string | null =>
  score === null || score === undefined ? null : SATISFACTION_LABELS[Number(score)] ?? `${score}/5`;

export function SatisfactionEstimate({ score, className }: { score: number | null | undefined; className?: string }) {
  const label = satisfactionLabel(score);
  if (!label) return <NotEvaluated className={className} />;
  const s = Number(score);
  const tone: Tone = s >= 4 ? "success" : s === 3 ? "muted" : "danger";
  const icon = s >= 4 ? Check : s === 3 ? Minus : X;
  return <StatusText tone={tone} icon={icon} className={className}>{label}</StatusText>;
}

/* ─── Réitération (déduite du verbatim) ─── */

export function ReiterationLabel({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return <NotEvaluated className={className} />;
  if (value === "Première itération") {
    return <span className={cn("text-sm text-muted-foreground", className)}>1er contact</span>;
  }
  const label = value === "Deuxième itération" ? "2e contact" : "3e contact ou +";
  return (
    <StatusText tone={value === "Deuxième itération" ? "warning" : "danger"} icon={Repeat} className={className} title={value}>
      {label}
    </StatusText>
  );
}

/* ─── Note sur 100 ─── */

export function scoreTone(value: number | null | undefined): Tone {
  if (value === null || value === undefined) return "muted";
  if (value >= 75) return "success";
  if (value >= 50) return "warning";
  return "danger";
}

/** Note sur 100 avec une courte barre ; la valeur reste lisible sans la couleur. */
export function QualityScore({ value, className, bar = true }: { value: number | null | undefined; className?: string; bar?: boolean }) {
  if (value === null || value === undefined) return <NotEvaluated className={className} />;
  const v = Math.round(value);
  const tone = scoreTone(v);
  return (
    <span className={cn("inline-flex items-center justify-end gap-2", className)}>
      {bar && (
        <span className="hidden xl:block h-1.5 w-10 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <span
            className={cn("block h-full rounded-full", tone === "success" ? "bg-success" : tone === "warning" ? "bg-warning" : "bg-destructive")}
            style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
          />
        </span>
      )}
      <span className="text-sm font-medium tabular-nums text-foreground">{formatScore(v)}</span>
    </span>
  );
}
